import { useMemo } from 'react';

import { DropdownPillComboboxSingleAdd } from 'ui-kit/Combobox/DropdownPillComboboxSingleAdd';
import clsx from 'clsx';
import { keyBy, chain } from 'lodash';
import { isArrayOf, isNotNullOrUndefined, isNumber, isString } from 'utils/typeUtils';

import { BooleanPillFilters } from './filterComponents/BooleanPillFilter';
import { DateRangePillFilter } from './filterComponents/DateRangePillFilter';
import { ListInputPillFilter } from './filterComponents/ListInputPillFilter';
import { MultiSelectPillFilter } from './filterComponents/MultiSelectPillFilter';
import { RangePillFilter } from './filterComponents/RangePillFilter';
import { SingleSelectPillFilter } from './filterComponents/SingleSelectPillFilter';
import {
  BaseFiltersConfig,
  BooleanFilter,
  DateRangeFilter,
  FilterValue,
  ListInputFilter,
  MultiSelectFilter,
  RangeFilter,
  SingleSelectFilter,
} from './filters.types';

export type PillFiltersProps<T extends BaseFiltersConfig> = {
  filtersConfig: T;
  currentFilters: FilterValue<T>[];
  setCurrentFilters: (filters: FilterValue<T>[]) => void;
  // True while the data these filters narrow down is being (re)fetched —
  // e.g. right after adding/editing a filter, before its query resolves.
  // Dims the current filter pills so the row reads as "applying" rather
  // than leaving stale-looking data on screen with no feedback.
  isLoading?: boolean;
  className?: string;
};

export function PillFilters<T extends BaseFiltersConfig>(props: PillFiltersProps<T>) {
  const allFiltersSorted = useMemo(
    () =>
      chain(props.filtersConfig)
        .map((value, key) => ({
          name: key as keyof T,
          ...value,
        }))
        .sortBy('displayName')
        .value(),
    [props.filtersConfig],
  );

  const currentMap = useMemo(() => keyBy(props.currentFilters, 'name'), [props.currentFilters]);

  const availableFilters = allFiltersSorted.filter((filter) => !currentMap[filter.name as string]);

  function removeFilter(_filterName: keyof T, index: number) {
    const newArr = [...props.currentFilters];
    newArr.splice(index, 1);
    props.setCurrentFilters(newArr);
  }

  function addFilter(filterConfig: (typeof availableFilters)[number]) {
    const newFilter = (() => {
      switch (filterConfig.type) {
        case 'boolean':
          return {
            name: filterConfig.name,
            value: false,
          };
        case 'range':
          return {
            name: filterConfig.name,
            min: filterConfig.min,
            max: filterConfig.max,
          };
        case 'date-range':
          return {
            name: filterConfig.name,
            min: filterConfig.min,
            max: filterConfig.max,
          };
        case 'select-single':
          return {
            name: filterConfig.name,
            value: null,
          };
        case 'select-multi':
          return {
            name: filterConfig.name,
            value: [],
          };
        case 'list-input':
          return {
            name: filterConfig.name,
            values: [],
          };
      }
    })();

    props.setCurrentFilters([...props.currentFilters, newFilter]);
  }

  function updateFilter(
    filterName: keyof T,
    index: number,
    values: [number, number] | string | boolean | string[],
  ) {
    const newArr = [...props.currentFilters];

    let val: Partial<FilterValue<T>>;
    if (isNotNullOrUndefined(values) && isArrayOf(isNumber)(values) && values.length === 2) {
      val = {
        min: values[0],
        max: values[1],
      };
    } else if (
      props.filtersConfig[filterName]?.type === 'list-input' &&
      isArrayOf(isString)(values)
    ) {
      // list-input is keyed by `values` (plural); every other filter type that can carry a
      // string array (select-multi) is keyed by `value` (singular) -- the array shape alone
      // doesn't distinguish them, so this has to branch on the filter's own declared type.
      // This used to check `filterName === FilterableField.account_id` specifically, which
      // silently broke every other list-input filter (e.g. a plain text-search field): its
      // typed values landed on `value`, which ListInputPillFilter's display and any filtering
      // logic keyed to `.values` never reads.
      val = { values };
    } else {
      val = { value: values };
    }

    newArr[index] = {
      ...(newArr[index] as FilterValue<T>),
      ...val,
    };

    props.setCurrentFilters(newArr);
  }

  // Applied directly to each pill's own className rather than to a wrapping
  // div around the group: PillFilters relies on every pill (this group,
  // the "Filter:" label, and the Add Filter button) being direct children
  // of one shared flex-wrap row so they wrap together as needed. A wrapper
  // around just this group breaks that — it becomes its own flex item, so
  // it (and whatever wraps inside it) drops to its own line instead of
  // flowing inline with the label/button, and the internal row spacing
  // stops matching the rest of the row's gap.
  const loadingPillClassName = clsx({
    'pointer-events-none animate-pulse opacity-50': props.isLoading,
  });

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-2 text-[12px] leading-[1.67] text-slate-400 [&>*]:m-0',
        props.className,
      )}
      aria-busy={props.isLoading || undefined}
    >
      <span>Filter:</span>{' '}
      {props.currentFilters.map((filter, index) => {
        const filterConfig =
          filter.name in props.filtersConfig ? props.filtersConfig[filter.name] : null;
        if (!filterConfig) return null;
        const itemProps = {
          removeFilter,
          updateFilter,
          index,
          key: filter.name as string,
        };
        switch (filterConfig.type) {
          case 'boolean':
            return (
              <BooleanPillFilters<T>
                {...itemProps}
                filter={filter as BooleanFilter<T>}
                filterConfig={filterConfig}
                className={loadingPillClassName}
              />
            );
          case 'range':
            return (
              <RangePillFilter<T>
                {...itemProps}
                rangeFilter={filter as RangeFilter<T>}
                filterConfig={filterConfig}
                className={loadingPillClassName}
              />
            );
          case 'date-range':
            return (
              <DateRangePillFilter<T>
                {...itemProps}
                dateRangeFilter={filter as DateRangeFilter<T>}
                filterConfig={filterConfig}
                className={loadingPillClassName}
              />
            );
          case 'select-single':
            return (
              <SingleSelectPillFilter<T>
                {...itemProps}
                filter={filter as SingleSelectFilter<T>}
                filterConfig={filterConfig}
                className={loadingPillClassName}
              />
            );
          case 'select-multi':
            return (
              <MultiSelectPillFilter<T>
                {...itemProps}
                filter={filter as MultiSelectFilter<T>}
                filterConfig={filterConfig}
                className={loadingPillClassName}
              />
            );
          case 'list-input':
            return (
              <ListInputPillFilter<T>
                {...itemProps}
                filter={filter as ListInputFilter<T>}
                filterConfig={filterConfig}
                className={loadingPillClassName}
              />
            );
          default:
            return null;
        }
      })}
      <DropdownPillComboboxSingleAdd
        options={availableFilters}
        onValueSelected={addFilter}
        buttonLabel="Add Filter"
        getOptionLabel={(option) => option.displayName}
        getOptionValue={(option) => option.name as string}
        comboLabel="Search for a filter"
        inputPlaceholder="Enter a filter name..."
        manuLabel="All filters (Ascending)"
        data-testid="pill-filters-add-filter-button"
      />
    </div>
  );
}

export type CommonProps<T> = {
  removeFilter: (filterName: keyof T, index: number) => void;
  updateFilter: (
    filterName: keyof T,
    index: number,
    values: [number, number] | string | boolean | string[],
  ) => void;
  index: number;
};
